import { Injectable, inject, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth-service';

export interface SubscriptionStatus {
  hasSubscription: boolean;
  isExpired: boolean;
  planName: string;
  expiryDate: string | null;
  daysRemaining: number | null;
  message: string;
  severity: 'none' | 'warning' | 'critical';
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionBannerService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private baseUrl = environment.apiUrl + '/billing/';
  private isBrowser: boolean;

  private statusSubject = new BehaviorSubject<SubscriptionStatus>({
    hasSubscription: true,
    isExpired: false,
    planName: '',
    expiryDate: null,
    daysRemaining: null,
    message: '',
    severity: 'none'
  });

  private fetched = false;
  private dismissed = false;

  status$ = this.statusSubject.asObservable();

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  /**
   * Fetches the subscription status for the current tenant.
   * Caches the result so subsequent calls don't re-fetch.
   */
  checkSubscriptionStatus(): Observable<SubscriptionStatus> {
    if (!this.isBrowser || !this.authService.loggedIn) {
      return of(this.statusSubject.value);
    }

    // Return cached result if already fetched
    if (this.fetched) {
      return of(this.statusSubject.value);
    }

    return this.http.post<any>(`${this.baseUrl}subscription/check-status/`, {}).pipe(
      map((response: any) => {
        const status = this.parseSubscriptionResponse(response);
        this.statusSubject.next(status);
        this.fetched = true;
        return status;
      }),
      catchError((error) => {
        // If the API fails (e.g., 404, 500), assume no subscription
        // This is a safe default since the backend may not have this endpoint yet
        const fallbackStatus: SubscriptionStatus = {
          hasSubscription: false,
          isExpired: false,
          planName: '',
          expiryDate: null,
          daysRemaining: null,
          message: 'Unable to verify subscription status. Please contact support if this persists.',
          severity: 'warning'
        };
        this.statusSubject.next(fallbackStatus);
        this.fetched = true;
        return of(fallbackStatus);
      })
    );
  }

  /**
   * Parses the backend response and returns a normalized SubscriptionStatus.
   */
  private parseSubscriptionResponse(response: any): SubscriptionStatus {
    // Expected response shape from backend:
    // { has_subscription: boolean, is_expired: boolean, plan_name: string,
    //   end_date: string|null, days_remaining: number|null, status: number }

    if (!response) {
      return {
        hasSubscription: false,
        isExpired: false,
        planName: '',
        expiryDate: null,
        daysRemaining: null,
        message: 'No subscription plan found. Please subscribe to continue using all features.',
        severity: 'critical'
      };
    }

    const hasSubscription = response.has_subscription === true;
    const isExpired = response.is_expired === true;
    const planName = response.plan_name || '';
    const expiryDate = response.end_date || null;
    const daysRemaining = response.days_remaining ?? null;

    let message = '';
    let severity: SubscriptionStatus['severity'] = 'none';

    if (!hasSubscription) {
      message = 'No active subscription plan found. Please subscribe to a plan to unlock all features.';
      severity = 'critical';
    } else if (isExpired) {
      message = `Your subscription plan "${planName}" has expired. Please renew to continue using all features.`;
      severity = 'critical';
    } else if (daysRemaining !== null && daysRemaining <= 7) {
      message = `Your subscription plan "${planName}" expires in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}. Please renew soon to avoid interruption.`;
      severity = 'warning';
    }

    return {
      hasSubscription,
      isExpired,
      planName,
      expiryDate,
      daysRemaining,
      message,
      severity
    };
  }

  private formatDate(dateStr: string | null): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return dateStr;
    }
  }

  /**
   * Dismiss the banner for this session
   */
  dismissBanner(): void {
    this.dismissed = true;
  }

  /**
   * Check if the banner has been dismissed
   */
  get isDismissed(): boolean {
    return this.dismissed;
  }

  /**
   * Force re-fetch subscription status (e.g., after a plan change)
   */
  refreshStatus(): void {
    this.fetched = false;
    this.dismissed = false;
    this.checkSubscriptionStatus().subscribe();
  }
}
